import React, {FC, useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import Module1ReportUploadModal from './Module1ReportUploadModal'
import Module1ReportLibraryModal from './Module1ReportLibraryModal'
import Module1ReportEditModal from './Module1ReportEditModal'
import {MonthEndReportRevisionsModal} from './MonthEndReportRevisionsModal'
import {
  listModule1ReportRevisions,
  module1ReportRevisionViewUrl,
  module1ReportRevisionDownloadUrl,
} from '../core/_requests'
import {toast, ToastContainer} from 'react-toastify'
import {useAuth} from '../../auth'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import {CertificateViewer} from './CertificateViewer'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { RemarksCell, RemarksModal } from './StructuredRemarksView'
import { ActionReviewNotePopover } from './ActionReviewNotePopover'

type ReportRow = {
  id: number
  certificateNameText?: string | null
  certificateName?: string | null
  name?: string | null
  fileName?: string | null
  certificateNameId?: number | null
  dateOfIssue?: string | null
  dateOfExpiry?: string | null
  revisionCount?: number | null
  uploadedByName?: string | null
  vesselId?: number | null
  remarks?: string | null
  reviewNote?: string | null
  filePath?: string | null
  fileSize?: string | null
  uploadedDate?: string | null
}

type ReportLibraryName = {
  id: number
  name: string
}

type SortKey = 'reportName' | 'uploadedDate' | 'uploadedByName' | 'remarks'
type SortConfig = {
  key: SortKey | ''
  direction: 'asc' | 'desc'
}

const MonthsEndReportPage: FC = () => {
  const {auth, currentUser} = useAuth()
  const {canRead, canCreate, canUpdate, canDelete, hasAssignedVessel} =
    useMonthEndFeatureAccess('module1-report')
  const canManageLibrary = canCreate || canUpdate || canDelete
  const isShipboardUser = hasAssignedVessel
  const accessDenied = !canRead

  /* if (!canRead) {
    return (
      <QhseAccessDenied message='Access denied — you do not have permission to view Month End Reports.' />
    )
  } */

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedVesselId, setSelectedVesselId] = useState(
    isShipboardUser && currentUser?.vessel?.id ? String(currentUser.vessel.id) : ''
  )

  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  const allowedCompanyVesselIds = useMemo(() => {
    if (!globalState.company) return null
    return new Set((globalVessels || []).map((gv: any) => String(gv.id)))
  }, [globalState.company, globalVessels])

  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchQuery(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    setSelectedVesselId(globalState.vessel?.id ? String(globalState.vessel.id) : '')
  }, [globalState.vessel])
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false)
  const [vessels, setVessels] = useState<Array<{id: string; name: string}>>([])
  const [reports, setReports] = useState<ReportRow[]>([])
  const [reportLibraryNames, setReportLibraryNames] = useState<ReportLibraryName[]>([])
  const [isLoadingReports, setIsLoadingReports] = useState(true)
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: 'asc'})
  const [refreshKey, setRefreshKey] = useState(0)
  const [revisionsModalId, setRevisionsModalId] = useState<number | null>(null)
  const [editingReport, setEditingReport] = useState<ReportRow | null>(null)
  const [viewingReport, setViewingReport] = useState<ReportRow | null>(null)
  const [viewingFile, setViewingFile] = useState<{
    name: string
    viewUrl: string
    downloadUrl: string
    uploadedDate?: string
    fileSize?: string
  } | null>(null)
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  useEffect(() => {
    const loadVessels = async () => {
      try {
        const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        setVessels(
          vesselList.map((vessel: any) => ({
            id: String(vessel.id),
            name: vessel.fleet_name || vessel.name || `Vessel #${vessel.id}`,
          }))
        )
      } catch (error) {
        console.error('Failed to load vessel list:', error)
        setVessels([])
      }
    }

    if (auth?.auth?.jwt) {
      if (isShipboardUser && currentUser?.vessel) {
        setVessels([{
          id: String(currentUser.vessel.id),
          name: (currentUser.vessel as any).fleet_name || (currentUser.vessel as any).name || `Vessel #${currentUser.vessel.id}`
        }])
      } else {
        loadVessels()
      }
    }
  }, [auth?.auth?.jwt, isShipboardUser, currentUser])

  const loadReports = async () => {
    if (!auth?.auth?.jwt) return

    setIsLoadingReports(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/qhse/module1-reports`, {
        headers: {Authorization: `Bearer ${auth.auth.jwt}`},
      })
      if (!response.ok) {
        throw new Error('Failed to load reports')
      }

      const data = (await response.json()) as ReportRow[]
      setReports(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load report list:', error)
      setReports([])
    } finally {
      setIsLoadingReports(false)
    }
  }

  useEffect(() => {
    loadReports()
  }, [auth?.auth?.jwt, refreshKey])

  const loadReportLibraryNames = async () => {
    if (!auth?.auth?.jwt) return

    try {
      const params = new URLSearchParams()
      params.append('activeOnly', 'true')
      params.append('page', '0')
      params.append('size', '1000')

      const response = await fetch(`${process.env.REACT_APP_API_URL}/qhse/module1-report-names/library?${params.toString()}`, {
        headers: {Authorization: `Bearer ${auth.auth.jwt}`},
      })
      if (!response.ok) {
        throw new Error('Failed to load report names')
      }

      const payload = await response.json()
      const content = Array.isArray(payload) ? payload : payload.content || []
      setReportLibraryNames(
        content.map((item: any) => ({
          id: item.id,
          name: item.name,
        }))
      )
    } catch (error) {
      console.error('Failed to load report library names:', error)
      setReportLibraryNames([])
    }
  }

  useEffect(() => {
    loadReportLibraryNames()
  }, [auth?.auth?.jwt, refreshKey])

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/qhse/module1-reports/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth?.auth?.jwt}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete report')
      }

      setRefreshKey((k) => k + 1)
    } catch (error) {
      console.error('Failed to delete report:', error)
      alert('Failed to delete report')
    }
  }

  const handleEditSubmit = async (payload: {
    id: number
    certificateName?: string
    certificateNameId?: number
    dateOfIssue?: string
    remarks?: string
    __file?: File | null
  }) => {
    setIsSavingEdit(true)
    try {
      const metaResponse = await fetch(`${process.env.REACT_APP_API_URL}/qhse/module1-reports/${payload.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth?.auth?.jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: payload.id,
          certificateName: payload.certificateName,
          dateOfIssue: payload.dateOfIssue,
          remarks: payload.remarks,
        }),
      })

      if (!metaResponse.ok) throw new Error('Failed to update report')

      if (payload.__file) {
        const form = new FormData()
        form.append('file', payload.__file)
        const fileResponse = await fetch(`${process.env.REACT_APP_API_URL}/qhse/module1-reports/${payload.id}/file`, {
          method: 'PATCH',
          headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
          body: form,
        })
        if (!fileResponse.ok) throw new Error('Failed to replace report file')
        // optimistic UI update: increment revision count so the history hexagon shows immediately
        setReports((prev) =>
          prev.map((r) =>
            r.id === payload.id
              ? {...r, revisionCount: (r.revisionCount ?? 0) + 1}
              : r
          )
        )
      }

      toast.success('Report updated')
      setEditingReport(null)
      setRefreshKey((k) => k + 1)
    } catch (error: any) {
      toast.error('Update failed')
    } finally {
      setIsSavingEdit(false)
    }
  }

  const filteredReports = useMemo(() => {
    const monthPrefix = selectedMonth ? `${selectedMonth}-` : ''
    const search = searchQuery.trim().toLowerCase()

    return reports.filter((report) => {
      if (allowedCompanyVesselIds && report.vesselId != null) {
        if (!allowedCompanyVesselIds.has(String(report.vesselId))) return false
      }

      const vesselMatches = !selectedVesselId || String(report.vesselId ?? '') === selectedVesselId

      const issueDate = report.dateOfIssue || report.uploadedDate || ''
      const monthMatches = !selectedMonth || issueDate.startsWith(monthPrefix)

      const reportName = (report.certificateName || report.name || report.certificateNameText || report.fileName || '').toLowerCase()
      const remarks = (report.remarks || '').toLowerCase()
      const uploadedBy = (report.uploadedByName || '').toLowerCase()
      const searchMatches =
        !search ||
        reportName.includes(search) ||
        remarks.includes(search) ||
        uploadedBy.includes(search)
      const libraryMatches = true

      return vesselMatches && monthMatches && searchMatches && libraryMatches
    })
  }, [reports, reportLibraryNames, searchQuery, selectedMonth, selectedVesselId])

  const handleSort = (key: SortKey) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({key, direction})
  }

  const sortedReports = useMemo(() => {
    const rows = [...filteredReports]
    if (!sortConfig.key) return rows

    rows.sort((a, b) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortConfig.key) {
        case 'reportName':
          aValue = (a.certificateName || a.certificateNameText || '').toLowerCase()
          bValue = (b.certificateName || b.certificateNameText || '').toLowerCase()
          break
        case 'uploadedDate':
          aValue = a.uploadedDate || a.dateOfIssue ? new Date(a.uploadedDate || a.dateOfIssue || '').getTime() : 0
          bValue = b.uploadedDate || b.dateOfIssue ? new Date(b.uploadedDate || b.dateOfIssue || '').getTime() : 0
          break
        case 'uploadedByName':
          aValue = (a.uploadedByName || '').toLowerCase()
          bValue = (b.uploadedByName || '').toLowerCase()
          break
        case 'remarks':
          aValue = (a.remarks || '').toLowerCase()
          bValue = (b.remarks || '').toLowerCase()
          break
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return rows
  }, [filteredReports, sortConfig])

  const vesselNameById = useMemo(() => {
    const map = new Map<string, string>()
    vessels.forEach((vessel) => map.set(vessel.id, vessel.name))
    return map
  }, [vessels])

  const formatMonthYear = (value?: string | null) => {
    if (!value) return '-'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return '-'
    return parsed.toLocaleString('en-US', {month: 'short', year: 'numeric'})
  }

  return accessDenied ? (
    <QhseAccessDenied message='Access denied â€” you do not have permission to view Month End Reports.' />
  ) : (
    <div
      className='app-main flex-column flex-row-fluid p-5'
      id='kt_app_main'
      style={{height: '100vh', overflowX: 'hidden'}}
    >
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid' style={{flex: 1}}>
          <div className='container-fluid'>
            
            <div
              className='card py-4'
              style={{backgroundColor: '#fff', boxShadow: 'none', outline: 'none'}}
            >
              <div className='card-header border-0 d-flex flex-column gap-3'>
                <span className='card-label fw-bold fs-3 mb-1'>Month End Reports</span>

                <span className='text-muted mt-1 fw-semibold fs-7'>
                  {selectedVesselId
                    ? `Reports for ${vessels.find((v) => v.id === selectedVesselId)?.name || 'Selected Vessel'}`
                    : 'Showing all reports'}
                </span>

                <div
                  className='d-flex align-items-center position-relative flex-grow-1 me-3 gap-3'
                  style={{flexWrap: 'wrap'}}
                >

                  <div className='d-flex align-items-center position-relative flex-grow-1 me-3 mt-3'>
                    <input
                      type='text'
                      className='form-control form-control-solid ps-5'
                      placeholder='Search reports...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className='mt-3' style={{minWidth: '180px'}}>
                    <label className='form-label fw-semibold fs-7 text-muted mb-1'>Month</label>
                    <input
                      type='month'
                      className='form-control form-control-solid'
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                  </div>

                  {canManageLibrary && (
                    <button
                      className='btn btn-light-primary mt-3'
                      type='button'
                      onClick={() => setIsLibraryModalOpen(true)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen048.svg'
                        className='svg-icon-2 me-2'
                      />
                      Report Library
                    </button>
                  )}

                  <button
                    className='btn btn-primary mt-3'
                    type='button'
                    onClick={() => setIsUploadModalOpen(true)}
                    disabled={!canCreate}
                  >
                    <KTSVG
                      path='/media/icons/duotune/arrows/arr075.svg'
                      className='svg-icon-2 me-2'
                    />
                    Add report
                  </button>
                </div>
              </div>

              <div className='card-body pt-0'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='text-center' style={{width: '70px'}}>
                          SR/NO
                        </th>
                        <th onClick={() => handleSort('reportName')} className='min-w-250px cursor-pointer'>
                          Report Name
                          <KTSVG
                            path={`/media/map/sort-col-${sortConfig.key === 'reportName'
                              ? sortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'}.svg`}
                            className='svg-icon ms-2 custom-sort-icon'
                          />
                        </th>
                        <th onClick={() => handleSort('uploadedDate')} className='min-w-120px cursor-pointer'>
                          Uploaded Date
                          <KTSVG
                            path={`/media/map/sort-col-${sortConfig.key === 'uploadedDate'
                              ? sortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'}.svg`}
                            className='svg-icon ms-2 custom-sort-icon'
                          />
                        </th>
                        <th onClick={() => handleSort('uploadedByName')} className='min-w-150px cursor-pointer'>
                          Uploaded By
                          <KTSVG
                            path={`/media/map/sort-col-${sortConfig.key === 'uploadedByName'
                              ? sortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'}.svg`}
                            className='svg-icon ms-2 custom-sort-icon'
                          />
                        </th>
                        <th className='min-w-120px'>File</th>
                        <th className='min-w-100px text-center'>Revisions</th>
                        <th 
                          onClick={() => handleSort('remarks')} 
                          className='min-w-150px cursor-pointer'
                          style={{minWidth: '160px', maxWidth: '240px'}}
                        >
                          Remarks
                          <KTSVG
                            path={`/media/map/sort-col-${sortConfig.key === 'remarks'
                              ? sortConfig.direction === 'asc'
                                ? 'up-black'
                                : 'down-black'
                              : 'grey'}.svg`}
                            className='svg-icon ms-2 custom-sort-icon'
                          />
                        </th>
                        <th className='min-w-160px text-center'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingReports ? (
                        <TableSkeleton rows={8} cols={8} />
                      ) : sortedReports.length === 0 ? (
                        <tr>
                          <td colSpan={8} className='text-center py-12 text-muted'>
                            No reports found for the selected filters.
                          </td>
                        </tr>
                      ) : (
                        sortedReports.map((report, index) => {
                          const uploadedDate = report.dateOfIssue || report.uploadedDate || '-'
                          const fileLabel = report.filePath ? 'Available' : '-'
                          return (
                            <tr key={report.id}>
                              <td className='text-center'>{index + 1}</td>
                              <td>{report.certificateName || report.name || report.certificateNameText || report.fileName || '-'}</td>
                              <td>{formatMonthYear(uploadedDate)}</td>
                              <td>{report.uploadedByName || '-'}</td>
                              <td>{fileLabel}</td>
                              <td className='text-center'>
                                <span className='badge badge-light-info'>
                                  {report.revisionCount ?? 0}
                                </span>
                              </td>
                              <td>
                                <RemarksCell
                                  text={report.remarks}
                                  title="Report Remarks"
                                  subtitle={report.certificateName || report.name || report.fileName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td className='text-center'>
                                <div className='d-flex align-items-center justify-content-center gap-1'>
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='View'
                                    onClick={async () => {
                                      try {
                                        const list: any[] = await listModule1ReportRevisions(report.id)
                                        const mapped = (list || []).map((r: any) => ({
                                          id: r.id,
                                          uploadedDate: r.uploadedDate,
                                          fileName: r.fileName || report.certificateName || 'report',
                                        }))
                                        mapped.sort((a, b) => {
                                          const ad = a?.uploadedDate ? new Date(a.uploadedDate).getTime() : 0
                                          const bd = b?.uploadedDate ? new Date(b.uploadedDate).getTime() : 0
                                          return bd - ad
                                        })
                                        const latest = mapped[0]
                                        if (latest) {
                                          setViewingFile({
                                            name: latest.fileName,
                                            viewUrl: module1ReportRevisionViewUrl(latest.id),
                                            downloadUrl: module1ReportRevisionDownloadUrl(latest.id),
                                            uploadedDate: latest.uploadedDate,
                                          })
                                        } else {
                                          toast.info('No revision available to view')
                                        }
                                      } catch (e: any) {
                                        toast.error('Failed to load revision')
                                      }
                                    }}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                  </button>
                                  

                                  {canUpdate && (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='Edit'
                                      onClick={() => setEditingReport(report)}
                                    >
                                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                    </button>
                                  )}

                                  <ActionReviewNotePopover
                                    reportId={report.id}
                                    reportName={report.certificateName || report.name || report.fileName || ''}
                                    initialReviewNote={report.reviewNote}
                                    canEdit={canUpdate}
                                    onReviewNoteUpdated={(newNote) => {
                                      setReports((prev) =>
                                        prev.map((r) =>
                                          r.id === report.id ? { ...r, reviewNote: newNote } : r
                                        )
                                      )
                                    }}
                                  />

                                  {(() => {
                                    const hasRevisions = (report.revisionCount ?? 0) > 0
                                    return (
                                      <button
                                        className='btn btn-icon btn-sm position-relative'
                                        title={hasRevisions ? `History (${report.revisionCount})` : 'History'}
                                        onClick={() => setRevisionsModalId(report.id)}
                                      >
                                        <KTSVG
                                          path='/media/icons/duotune/abstract/abs026.svg'
                                          className={`svg-icon-3 ${hasRevisions ? 'text-info' : 'text-muted'}`}
                                        />
                                        {hasRevisions && (
                                          <span
                                            className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'
                                            style={{ fontSize: 10 }}
                                          >
                                            {report.revisionCount}
                                          </span>
                                        )}
                                      </button>
                                    )
                                  })()}

                                  {canDelete && (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='Delete'
                                      onClick={() => handleDelete(report.id)}
                                    >
                                      <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Module1ReportUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false)
          setRefreshKey((k) => k + 1)
        }}
        selectedVesselId={selectedVesselId}
      />

      <Module1ReportLibraryModal
        isOpen={isLibraryModalOpen}
        onClose={() => {
          setIsLibraryModalOpen(false)
          setRefreshKey((k) => k + 1)
        }}
      />

      <Module1ReportEditModal
        isOpen={!!editingReport}
        report={editingReport}
        reportNames={reportLibraryNames}
        vesselName={
          editingReport?.vesselId != null
            ? vesselNameById.get(String(editingReport.vesselId))
            : undefined
        }
        isSubmitting={isSavingEdit}
        onClose={() => setEditingReport(null)}
        onViewFile={(report) => setViewingReport(report)}
        onSubmit={handleEditSubmit}
      />

      {viewingReport && (
        <CertificateViewer
          file={{
            name: viewingReport.certificateName || viewingReport.certificateNameText || 'Month End Report',
            viewUrl: `${process.env.REACT_APP_API_URL}/qhse/module1-reports/view/${viewingReport.id}`,
            downloadUrl: `${process.env.REACT_APP_API_URL}/qhse/module1-reports/download/${viewingReport.id}`,
            uploadedDate: viewingReport.uploadedDate || viewingReport.dateOfIssue || '',
            fileSize: viewingReport.fileSize || undefined,
          }}
          onClose={() => setViewingReport(null)}
        />
      )}

      {viewingFile && (
        <CertificateViewer
          file={{
            name: viewingFile.name,
            viewUrl: viewingFile.viewUrl,
            downloadUrl: viewingFile.downloadUrl,
            uploadedDate: viewingFile.uploadedDate || '',
            fileSize: viewingFile.fileSize,
          }}
          onClose={() => setViewingFile(null)}
        />
      )}

      {/* Revisions Modal */}
      {revisionsModalId && (
        <MonthEndReportRevisionsModal
          reportId={revisionsModalId}
          reportType='months-end-report'
          visible={!!revisionsModalId}
          onClose={() => setRevisionsModalId(null)}
        />
      )}

      <RemarksModal
        visible={remarksModal.visible}
        title={remarksModal.title}
        subtitle={remarksModal.subtitle}
        remarksText={remarksModal.text}
        onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
      />

      <ToastContainer />
    </div>
  )
}

export default MonthsEndReportPage
export { MonthsEndReportPage }
